// Every step of a workflow compiled into the form it will be captured through,
// one step per tab — "does this workflow ask what I meant it to".
//
// Rendered on TWO screens, which is why it is a component rather than the two
// copies it started as: the editor's Preview tab, where the steps being
// previewed are the unsaved ones in the form's state, and the workflow's own
// screen, where they are the stored ones. Both compile the same declaration
// through the same runtime, so a difference between them could only ever be a
// bug.
//
// NOT read-only on the view screen, and that is deliberate. `readOnly` renders
// each widget's View rather than its Edit — the stored VALUE instead of the
// control — and a preview has no values: it would be a column of labels beside
// a column of nothing, which says less about the workflow than the steps table
// next to it already does. What a person wants from a preview is the controls,
// so the fields are live and what is typed into them is thrown away. Nothing
// here is a capture: there is no capture path yet, and when there is one it
// will hand `step-form` real values and a real onSubmit without this component
// changing.
import React, { useState } from "react";
import { Form as StepForm } from "@shared/step-form";
import { missingSubforms } from "@common/schema/types";
import { StepTabs } from "components/step-tabs";

/**
 * One step's compiled form, or the reason it cannot be compiled.
 *
 * An `object` field naming a subform that was renamed or removed would
 * otherwise throw from inside the form's render and take the whole screen with
 * it — including, on the editor, the one screen the mistake can be undone on.
 */
const StepPreview = ({ step, subforms, widgets, values, onChange }) => {
  const missing = missingSubforms(step, subforms);

  if (missing.length > 0) {
    return (
      <p className="text-danger">
        This step asks for a subform that is not declared:{" "}
        <code>{missing.join(", ")}</code>. Add it on the Subforms tab, or point
        the field at one that exists.
      </p>
    );
  }

  return (
    <StepForm
      step={step}
      subforms={subforms}
      widgets={widgets}
      values={values}
      onChange={onChange}
    />
  );
};

/**
 * The steps, in order, one per tab, the selected one as a form.
 *
 * `empty` is what to say when there are none — the two screens can do something
 * about it in different places, so neither message would be right on both.
 */
export const Preview = ({ steps = [], subforms = [], widgets, empty }) => {
  // Keyed by step NAME rather than by position, so reordering the steps keeps
  // what was typed in each — and so does moving between the tabs, which
  // remounts the step's form over what this holds for it. Local, and thrown
  // away on unmount.
  const [filled, setFilled] = useState({});

  if (steps.length === 0) {
    return <p className="text-muted mt-3">{empty}</p>;
  }

  return (
    <div id="workflow-preview" className="mt-3">
      <StepTabs steps={steps}>
        {(step, index) => (
          <div key={step.name || index}>
            <h5 className="mb-1">{step.label || step.name}</h5>
            {step.description ? (
              <p className="text-muted">{step.description}</p>
            ) : null}
            <StepPreview
              step={step}
              subforms={subforms}
              widgets={widgets}
              values={filled[step.name] || {}}
              onChange={(vals) => setFilled({ ...filled, [step.name]: vals })}
            />
          </div>
        )}
      </StepTabs>
    </div>
  );
};

export default Preview;
